/**
 * AddToVault — THE ONE add surface, hosted twice.
 *
 * V-ADD. Desktop had an `AddItemModal` (a free-text form with no catalog search, posting
 * `import_source='manual'` rows that were never linked to an issue, and swallowing every failure
 * into `console.error`). Mobile had QuickAdd (search/scan/capture, wired to the catalog). Two add
 * surfaces, two contracts, one endpoint. This component is the tab body of BOTH: VaultPage mounts
 * it as its Add modal, QuickAdd mounts it under its ResponsiveLayout/header.
 *
 * It is route-agnostic ON PURPOSE. VaultPage renders without a Router (both of its suites do), so
 * nothing in here may import react-router: navigation is a prop (`onCapture`), telemetry is a prop
 * (`onTrack`), and the host owns close/back.
 *
 * Tabs, in order:
 *   Search   (DEFAULT) search -> select -> confirm. A result set of one is still a set: the count
 *            line says "1 match — confirm it is the right issue" and NOTHING is auto-selected.
 *   Scan     html5-qrcode -> scanBarcode(auto_confirm: false) -> BarcodeConfirmDialog. If the
 *            barcode route fails, the text-search fallback rows are NORMALISED to the dialog's
 *            candidate contract ({comic_id, title, issue_number, publisher, cover_url,
 *            confidence: null}) — the dialog used to receive raw search rows, compute
 *            match_type 'product' with match_id undefined, and 422 on confirm.
 *   Capture  a hand-off to /maestro/grade/upload via `onCapture` (no second camera).
 *   Bulk     BulkBarcodeImport as a tab (preview -> pick -> confirm -> undo).
 *   Manual   LAST. OP-1 HELD: the operator has not decided whether off-catalog adds are retired,
 *            so the old free-text fields survive here, labelled as not linked to the catalog, with
 *            a one-tap jump to Search. Posts without comic_issue_id; the server requires a title
 *            and so does the Save button.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ScanLine, Camera, Search, Plus, X, Loader2, Upload, Layers, Edit3, Check } from 'lucide-react';
import BarcodeScanner from '../shared/BarcodeScanner';
import { searchComicsForVault, addVaultItem, scanBarcode, getIssueCredits } from '../../services/vaultApi';
import { detectBarcodeType } from '../../services/scannerDB';
import { buildVaultIdentity, parseCoverYear } from '../../utils/gradeToVaultItem';
import { GRADE_OPTIONS as VAULT_GRADE_OPTIONS } from '../../utils/vaultGrade';
import BarcodeConfirmDialog from './BarcodeConfirmDialog';
import BulkBarcodeImport from './BulkBarcodeImport';

// The 10-point grading scale as collectors actually use it (CGC/CBCS). The old list was
// `(i + 1) * 0.5`, which offered 9.5 (not a grade on the scale) and had no 9.2 / 9.4 / 9.6 / 9.8 —
// the four grades most modern books are slabbed at. ONE list: the same utils/vaultGrade
// GRADE_OPTIONS (string form of the canonical CGC_GRADE_SCALE) that ItemDetail and
// VaultDetailPane offer, with 'Raw' in front for an ungraded add.
export const GRADE_OPTIONS = Object.freeze(['Raw', ...VAULT_GRADE_OPTIONS]);

export const SOURCE_OPTIONS = [
  { value: 'lcs', label: 'Local Comic Shop' },
  { value: 'online', label: 'Online Purchase' },
  { value: 'convention', label: 'Convention' },
  { value: 'gift', label: 'Gift' },
  { value: 'trade', label: 'Trade' },
  { value: 'auction', label: 'Auction' },
  { value: 'other', label: 'Other' },
];

const SEARCH_DEBOUNCE_MS = 300;

// V-SEARCH-PAGE: the endpoint's declared maximum (`page_size` le=50). Asking for the largest legal
// page is the cheapest half of this fix — "amazing spider-man" is 5,727 rows, which is 287 pages at
// the default 20 and 115 at 50. Raising it further is not ours to choose: 51 is a 422.
export const SEARCH_PAGE_SIZE = 50;

export const TAB_IDS = ['search', 'scan', 'capture', 'bulk', 'manual'];

/**
 * Resolve a requested tab (a prop, a `?tab=` param, a palette hint) to one that exists here.
 * Unknown values and a hidden Capture tab both fall back to Search — the default.
 */
export function normalizeTab(requested, captureEnabled) {
  if (!TAB_IDS.includes(requested)) return 'search';
  if (requested === 'capture' && !captureEnabled) return 'search';
  return requested;
}

/**
 * The confirm dialog's candidate contract, built from a SERVED `/comics/issues/search` row.
 * `confidence: null` — a text-search hit has no score, and the dialog renders no badge for it.
 */
export function searchRowToCandidate(row) {
  return {
    comic_id: row.id,
    title: row.title,
    issue_number: row.number,
    publisher: row.publisher_name,
    cover_url: row.cover_url,
    confidence: null,
  };
}

/**
 * The server's validation envelope (app/core/error_handler.py) joins Pydantic's `loc` with ' -> ':
 *   'body -> title: String should have at most 255 characters'   (a FIELD error, loc = body, title)
 *   'body: Value error, title is required when comic_issue_id is not provided (...)'
 *                                                                  (a MODEL validator, loc = body)
 * fetchAPI's own flattening of a native FastAPI 422 reads 'title: ...'. All three shapes are lifted
 * here: `field` names the input to mark (null when no input is to blame) and `message` is the human
 * part, without the loc prefix.
 */
const SERVED_LOC_PREFIX = /^body(?: -> ([a-z_]+))?: (?:Value error, )?/;
const FLATTENED_FIELD_PREFIX = /^([a-z_]+): /;
const NAMED_REQUIRED_FIELD = /\b([a-z_]+) is required\b/;

export function fieldFromValidationMessage(message) {
  const served = SERVED_LOC_PREFIX.exec(message);
  if (served) {
    const rest = message.slice(served[0].length);
    if (served[1]) return { field: served[1], message: rest };
    const named = NAMED_REQUIRED_FIELD.exec(rest);
    return { field: named ? named[1] : null, message: rest };
  }
  const flattened = FLATTENED_FIELD_PREFIX.exec(message);
  return { field: flattened ? flattened[1] : null, message };
}

/**
 * Turn a failed write into something the user can act on: on a 422 the offending field is lifted
 * out of the served message so the form can mark the input; anything else is shown as-is.
 */
export function describeSubmitError(err, fallback) {
  const message = err && typeof err.message === 'string' && err.message ? err.message : fallback;
  if (err && err.status === 422) {
    return fieldFromValidationMessage(message);
  }
  return { field: null, message };
}

const MANUAL_INITIAL = {
  title: '',
  issue_number: '',
  publisher: '',
  year: '',
  user_grade: '',
  is_graded: false,
  grading_company: '',
  cert_number: '',
  is_signed: false,
  signature_details: '',
};

/** Cover thumbnail with a placeholder for a missing OR broken image. */
function CoverThumb({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`${className} bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0`} data-testid="cover-placeholder">
        <Layers size={16} className="text-zinc-600" aria-hidden="true" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className={`${className} object-cover rounded-lg flex-shrink-0`}
    />
  );
}

function FieldError({ error, name }) {
  if (!error || error.field !== name) return null;
  return (
    <p className="text-red-400 text-xs mt-1" data-testid={`field-error-${name}`}>
      {error.message}
    </p>
  );
}

const inputClass =
  'w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-orange-500';
const selectStyle = { height: '48px', fontSize: '16px' };
// V-SEARCH-PAGE. `min-w/min-h-[44px]` is the touch target this component holds everywhere else —
// the pager is reached on a phone at least as often as on a desktop. `disabled:` styling is
// explicit so a bounds-disabled arrow reads as unavailable rather than as unresponsive.
/**
 * The year to show for a catalog row, and WHICH DATE IT COMES FROM.
 *
 * ⛔⛔ `cover_date` IS THE WRONG SOURCE, AND IT IS WHY EVERY RESULT READ "2026".
 * Comics carry TWO dates: a cover date (which runs months AHEAD of release) and an on-sale /
 * street date. The served `cover_date` is NULL on 99.77% of the catalogue, and 83.1% of the rows
 * that DO have one come from a single ingest batch that copied the street date into it — all
 * stamped 2026. So the only rows showing a year were showing a WRONG year from a poisoned batch.
 *
 * ⭐ `store_date` IS THE STREET DATE and is far better populated: 40.15% of the catalogue against
 * cover_date's 0.23%, and 94.34% within the "amazing spider-man" set. Verified against real rows:
 * ASM #1 store_date 1962-12-10 vs publication_date "March 1963"; ASM #129 1973-10-30 vs
 * "February 1974" — the classic ~3-month cover lead.
 *
 * cover_date remains the FALLBACK rather than being dropped: a row that has only a cover_date
 * should still show its year, and preferring store_date is what fixes the common case.
 */
export function displayYear(row) {
  return parseCoverYear(row?.store_date) ?? parseCoverYear(row?.cover_date);
}

/**
 * The credit roles the confirm card renders, in the order a collector reads them.
 *
 * ⭐ COVER ARTIST LEADS, AND IT IS NOT THE PENCILLER. The cover is what a collector recognises a
 * book BY — McFarlane on ASM #300 is the variant-identifier, and he is credited separately from
 * whoever pencilled the interior. Collapsing the two would attribute the wrong person to the one
 * detail most likely to confirm or deny a match.
 *
 * The first three are the MINIMUM the card must show when the data has them; colorist and editor
 * are shown when present.
 */
export const CREDIT_ROLES = [
  ['cover_artist', 'Cover'],
  ['writer', 'Writer'],
  ['penciller', 'Pencils'],
  ['colorist', 'Colors'],
  ['editor', 'Editor'],
];

const pagerButtonClass =
  'min-w-[44px] min-h-[44px] px-3 rounded-lg bg-zinc-800 text-white text-xs border border-zinc-700 ' +
  'hover:border-orange-500/40 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-700';

export default function AddToVault({
  onAdded,
  onClose,
  initialTab = 'search',
  showCapture = true,
  onCapture,
  onTrack,
}) {
  const captureEnabled = showCapture && typeof onCapture === 'function';
  const [activeTab, setActiveTab] = useState(() => normalizeTab(initialTab, captureEnabled));

  // The host can re-open the surface with a different hint (palette: "Scan Barcode" vs "Add").
  useEffect(() => {
    setActiveTab(normalizeTab(initialTab, captureEnabled));
  }, [initialTab, captureEnabled]);

  // ---- shared write state ----
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [added, setAdded] = useState(null); // {title, result}

  // ---- search ----
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = nothing searched yet
  const [searchLoading, setSearchLoading] = useState(false);
  // V-SEARCH-PAGE: 1-based, matching the endpoint. Everything past the first page used to be
  // UNREACHABLE — the search fetched page 1 and nothing rendered a way to ask for page 2, so a
  // user whose book sat at result 21 of 5,727 simply could not add it.
  const [searchPage, setSearchPage] = useState(1);
  // V-CONFIRM-CREDITS: null = not loaded (or loading); {} = the catalogue genuinely holds none.
  // Those are DIFFERENT states and the card renders them differently — collapsing them would show
  // "no credits" while a fetch was still in flight.
  const [credits, setCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [selectedComic, setSelectedComic] = useState(null);
  const [grade, setGrade] = useState('Raw');
  const [acquisitionPrice, setAcquisitionPrice] = useState('');
  const [source, setSource] = useState('');
  const [notes, setNotes] = useState('');

  // ---- scan ----
  const [scanResult, setScanResult] = useState(null); // {not_found, barcode} | {added, title}
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [showBarcodeConfirm, setShowBarcodeConfirm] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState(null);
  const [barcodeCandidates, setBarcodeCandidates] = useState([]);
  // The query the last completed fetch ran for — lets the effect tell a NEW SEARCH (debounce) from
  // a PAGE TURN (no debounce) without a second effect or a second piece of state.
  const lastSearchedQuery = useRef('');

  // ---- manual ----
  const [manual, setManual] = useState(MANUAL_INITIAL);

  const track = useCallback((name, domain, payload) => {
    if (typeof onTrack === 'function') onTrack(name, domain, payload);
  }, [onTrack]);

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    setScanResult(null);
    setSubmitError(null);
    setAdded(null);
  };

  // ------- SEARCH -------
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults(null);
      return undefined;
    }
    // ⚠ TYPING DEBOUNCES; TURNING A PAGE DOES NOT. Debouncing a pager click makes the control
    // feel broken — you press Next and nothing happens for a third of a second. The delay exists
    // to avoid a request per keystroke, which is a property of the QUERY changing, not the page.
    const isNewQuery = q !== lastSearchedQuery.current;
    const timer = setTimeout(async () => {
      lastSearchedQuery.current = q;
      setSearchLoading(true);
      try {
        const response = await searchComicsForVault(q, {
          page: searchPage,
          pageSize: SEARCH_PAGE_SIZE,
        });
        // GET /comics/issues/search answers {results, count, page, page_size, query}; anything
        // else is not a result set and renders as none.
        const items = Array.isArray(response.results) ? response.results : [];
        const count = typeof response.count === 'number' ? response.count : items.length;
        // ⛔ THE SERVED page/page_size WIN over what we asked for. If the endpoint ever clamps or
        // ignores a parameter, trusting our own request would render "Page 4 of 115" over page 1's
        // rows — a pager that lies about where you are is worse than no pager, because the user
        // concludes their book is not in the catalog.
        const page = typeof response.page === 'number' ? response.page : searchPage;
        const pageSize = typeof response.page_size === 'number' ? response.page_size : SEARCH_PAGE_SIZE;
        setSearchResults({ items, count, page, pageSize });
      } catch {
        setSearchResults({ items: [], count: 0, page: 1, pageSize: SEARCH_PAGE_SIZE });
      } finally {
        setSearchLoading(false);
      }
    }, isNewQuery ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [searchQuery, searchPage]);

  /**
   * ⛔ ONLY FOR THE SELECTED CANDIDATE, NEVER FOR THE LIST. The endpoint joins three credit tables
   * (issue_creators 3.5M rows, comic_story_creators 11M); running it per result would pay that
   * fifty times a page for cards nobody opens.
   *
   * ⚠ A FAILED FETCH IS NOT "NO CREDITS". It leaves `credits` null, which renders as nothing at
   * all rather than as a confident "no credits held" — the card must never assert the catalogue is
   * empty on the strength of a network error.
   */
  const loadCredits = useCallback(async (issueId) => {
    setCredits(null);
    if (!issueId) return;
    setCreditsLoading(true);
    try {
      const res = await getIssueCredits(issueId);
      setCredits(res && typeof res.credits === 'object' && res.credits !== null ? res.credits : {});
    } catch {
      setCredits(null);
    } finally {
      setCreditsLoading(false);
    }
  }, []);

  const handleSelectComic = (comic) => {
    setSelectedComic(comic);
    loadCredits(comic?.id);
    setGrade('Raw');
    setAcquisitionPrice('');
    setSource('');
    setNotes('');
    setSubmitError(null);
  };

  const handleSubmitCatalog = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Field names are the contract (buildVaultIdentity is shared with the grade flow's Save so
      // the two writers into POST /vault/items cannot drift apart again).
      const itemData = {
        ...buildVaultIdentity(selectedComic),
        // `user_grade` is a STRING (max 10). 'Raw' is an ABSENT grade, not the text "Raw".
        user_grade: grade === 'Raw' ? null : String(grade).slice(0, 10),
        purchase_price: acquisitionPrice ? parseFloat(acquisitionPrice) : null,
        purchase_location: source || null,
        notes: notes || null,
      };
      const result = await addVaultItem(itemData);
      track('vault.item.added', 'vault', {
        comic_issue_id: itemData.comic_issue_id,
        grade,
        source: source || 'unknown',
        input_method: 'search',
      });
      setAdded({ title: selectedComic.title, result });
      setSelectedComic(null);
      setSearchQuery('');
      setSearchResults(null);
      if (typeof onAdded === 'function') onAdded(result);
    } catch (err) {
      setSubmitError(describeSubmitError(err, 'Failed to add item to vault'));
    } finally {
      setSubmitting(false);
    }
  };

  // ------- SCAN -------
  // The scanner renders its own reason (blocked / no camera / other); this keeps the tab's own
  // hard-fail line so the failure is stated in the flow the user is actually in.
  const handleScannerError = useCallback(() => {
    setScanError('The camera scanner could not start. Use Search, or type the barcode there.');
  }, []);

  const handleDecoded = useCallback(async (decodedText) => {
    const barcodeType = detectBarcodeType(decodedText);
    track('vault.item.scanned', 'vault', { barcode: decodedText, barcode_type: barcodeType });
    setScanLoading(true);
    try {
      // auto_confirm: false — a >=90% match is a PROPOSAL the user confirms, never a commit the
      // server makes on their behalf (BETA-COMMERCE-298).
      const result = await scanBarcode(decodedText, { barcode_type: barcodeType, auto_confirm: false });
      if (result.matched && result.candidates.length > 0) {
        setPendingBarcode(decodedText);
        setBarcodeCandidates(result.candidates);
        setShowBarcodeConfirm(true);
        setScanResult(null);
      } else {
        setScanResult({ not_found: true, barcode: decodedText });
      }
    } catch {
      // Barcode route down: fall back to a text search, but the hits go through the SAME confirm
      // dialog, normalised to its candidate contract — a set of one is still a set.
      try {
        const response = await searchComicsForVault(decodedText);
        const items = Array.isArray(response.results) ? response.results : [];
        if (items.length > 0) {
          setPendingBarcode(decodedText);
          setBarcodeCandidates(items.map(searchRowToCandidate));
          setShowBarcodeConfirm(true);
          setScanResult(null);
        } else {
          setScanResult({ not_found: true, barcode: decodedText });
        }
      } catch {
        setScanResult({ not_found: true, barcode: decodedText });
      }
    } finally {
      setScanLoading(false);
    }
  }, [track]);


  const closeBarcodeConfirm = () => {
    setShowBarcodeConfirm(false);
    setPendingBarcode(null);
    setBarcodeCandidates([]);
  };

  // ------- MANUAL -------
  const manualTitleOk = manual.title.trim().length > 0;
  const setManualField = (name, value) => setManual((prev) => ({ ...prev, [name]: value }));

  const handleSubmitManual = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const signatureDetails = manual.signature_details.trim();
      const payload = {
        title: manual.title.trim(),
        issue_number: manual.issue_number || null,
        publisher: manual.publisher || null,
        year: manual.year ? parseInt(manual.year, 10) : null,
        user_grade: manual.user_grade || null,
        is_graded: manual.is_graded,
        grading_company: manual.is_graded ? manual.grading_company || null : null,
        cert_number: manual.is_graded ? manual.cert_number || null : null,
        is_signed: manual.is_signed,
        // Optional[dict] on the server: empty becomes null, a name is wrapped.
        signature_details: manual.is_signed && signatureDetails ? { signed_by: signatureDetails } : null,
      };
      const result = await addVaultItem(payload);
      track('vault.item.added', 'vault', { comic_issue_id: null, input_method: 'manual' });
      setAdded({ title: payload.title, result });
      setManual(MANUAL_INITIAL);
      if (typeof onAdded === 'function') onAdded(result);
    } catch (err) {
      setSubmitError(describeSubmitError(err, 'Failed to add item to vault'));
    } finally {
      setSubmitting(false);
    }
  };

  // ------- RENDER -------
  const tabs = [
    { id: 'search', label: 'Search', Icon: Search },
    { id: 'scan', label: 'Scan', Icon: ScanLine },
    ...(captureEnabled ? [{ id: 'capture', label: 'Capture', Icon: Camera }] : []),
    { id: 'bulk', label: 'Bulk Barcode', Icon: Upload },
    { id: 'manual', label: 'Manual', Icon: Edit3 },
  ];

  const renderAddedCard = () => (
    <div
      data-testid="added-card"
      className="bg-green-900/20 rounded-xl border border-green-800/30 p-4 text-center space-y-3"
    >
      <p className="text-green-400 font-semibold">Added to Vault</p>
      <p className="text-white text-sm">{added.title}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setAdded(null)}
          className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl py-2.5 text-sm transition-colors"
          style={{ minHeight: '44px' }}
          data-testid="add-another"
        >
          Add another
        </button>
        {typeof onClose === 'function' && (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-2.5 text-sm font-medium transition-colors"
            style={{ minHeight: '44px' }}
            data-testid="add-done"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );

  const renderCatalogForm = () => (
    <div data-testid="add-form" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold">Confirm and add</h3>
        <button
          type="button"
          data-testid="add-form-close"
          onClick={() => setSelectedComic(null)}
          className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
          style={{ minWidth: '44px', minHeight: '44px' }}
          aria-label="Back to results"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      <div className="bg-zinc-800/60 rounded-xl border border-zinc-700 p-3 flex gap-3">
        <CoverThumb src={selectedComic.cover_url} alt={selectedComic.title} className="w-12 h-16" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white truncate text-sm">{selectedComic.title}</p>
          <p className="text-xs text-zinc-400">
            {selectedComic.publisher_name || 'Unknown publisher'}
            {displayYear(selectedComic) ? ` · ${displayYear(selectedComic)}` : ''}
          </p>

          {/* ⭐ THE CONFIRM LEG'S WHOLE JOB IS VERIFICATION. Publisher and year narrow a book;
              the CREDITS are what actually confirm it — a collector knows ASM #300 by McFarlane's
              cover long before they check an issue number. Coverage is partial by nature (cover
              artist reaches 20.84% of the catalogue even after unioning every source), so this is
              "show if present" and must degrade without leaving a hole in the card. */}
          {creditsLoading && (
            <p className="text-xs text-zinc-500 mt-1" data-testid="credits-loading">
              Loading credits…
            </p>
          )}
          {!creditsLoading && credits && Object.keys(credits).length > 0 && (
            <dl className="mt-1 space-y-0.5" data-testid="credits">
              {CREDIT_ROLES.filter(([role]) => credits[role]?.length).map(([role, label]) => (
                <div key={role} className="flex gap-1.5 text-xs leading-tight">
                  <dt className="text-zinc-500 flex-shrink-0">{label}</dt>
                  <dd className="text-zinc-300 truncate" data-testid={`credit-${role}`}>
                    {credits[role].join(', ')}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {/* ⚠ `{}` means the catalogue HOLDS none — an honest, useful statement. `null` means the
              fetch is unfinished or failed, and renders NOTHING: the card must never claim the
              catalogue is empty on the strength of a network error. */}
          {!creditsLoading && credits && Object.keys(credits).length === 0 && (
            <p className="text-xs text-zinc-500 mt-1" data-testid="credits-none">
              No creator credits on file
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="grade-select" className="block text-sm text-zinc-400 mb-1">Grade</label>
        <select
          id="grade-select"
          data-testid="grade-select"
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          className={`${inputClass} appearance-none`}
          style={selectStyle}
        >
          {GRADE_OPTIONS.map((g) => (
            <option key={g} value={g} className="bg-zinc-900 text-white">{g}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="price-input" className="block text-sm text-zinc-400 mb-1">Acquisition Price</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
          <input
            id="price-input"
            data-testid="price-input"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={acquisitionPrice}
            onChange={(e) => setAcquisitionPrice(e.target.value)}
            placeholder="0.00"
            className={`${inputClass} pl-7`}
            style={selectStyle}
          />
        </div>
      </div>

      <div>
        <label htmlFor="source-select" className="block text-sm text-zinc-400 mb-1">Source</label>
        <select
          id="source-select"
          data-testid="source-select"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className={`${inputClass} appearance-none`}
          style={selectStyle}
        >
          <option value="" className="bg-zinc-900 text-white">Select source...</option>
          {SOURCE_OPTIONS.map((s) => (
            <option key={s.value} value={s.value} className="bg-zinc-900 text-white">{s.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="notes-input" className="block text-sm text-zinc-400 mb-1">Notes</label>
        <textarea
          id="notes-input"
          data-testid="notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Condition notes, variant details, etc."
          rows={3}
          className={`${inputClass} py-3 resize-none`}
          style={{ fontSize: '16px' }}
        />
      </div>

      {submitError && (
        <p data-testid="submit-error" role="alert" className="text-red-400 text-sm">
          {submitError.message}
        </p>
      )}

      <button
        type="button"
        data-testid="submit-add"
        onClick={handleSubmitCatalog}
        disabled={submitting}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ height: '52px', fontSize: '16px' }}
      >
        {submitting ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
        {submitting ? 'Adding...' : 'Add to Vault'}
      </button>
    </div>
  );

  const renderSearchTab = () => {
    if (added) return renderAddedCard();
    if (selectedComic) return renderCatalogForm();
    const count = searchResults ? searchResults.count : 0;
    const pageSize = searchResults?.pageSize || SEARCH_PAGE_SIZE;
    const page = searchResults?.page || 1;
    // Ceiling, and never below 1 — a 0-result set is "page 1 of 1", not "page 1 of 0".
    const totalPages = Math.max(1, Math.ceil(count / pageSize));
    const firstShown = count === 0 ? 0 : (page - 1) * pageSize + 1;
    const lastShown = Math.min(count, page * pageSize);
    const goToPage = (next) => {
      const clamped = Math.min(totalPages, Math.max(1, next));
      if (clamped !== page) setSearchPage(clamped);
    };
    return (
      <div data-testid="search-tab-content">
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            data-testid="search-input"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              // ⛔ A NEW QUERY ALWAYS STARTS AT PAGE 1. Without this, refining a search from page 7
              // asks the endpoint for page 7 of a set that may only have two pages — and an
              // out-of-range offset returns ZERO rows, which reads to the user as "my book is not
              // in the catalog" when it is sitting on page 1.
              setSearchPage(1);
            }}
            placeholder="Search by title, issue, or barcode..."
            autoFocus
            className={`${inputClass} pl-10`}
            style={selectStyle}
          />
        </div>

        {searchLoading && (
          <div className="flex justify-center py-8" role="status">
            <Loader2 size={24} className="animate-spin text-orange-500" />
            <span className="sr-only">Searching...</span>
          </div>
        )}

        {!searchLoading && searchResults && searchResults.items.length > 0 && (
          <div className="space-y-2" data-testid="search-results">
            <p className="text-xs text-zinc-400" data-testid="search-count">
              {count === 1
                ? '1 match — confirm it is the right issue'
                : `${count} matches — pick the right issue`}
              {totalPages > 1 && (
                <span data-testid="search-range">{` · showing ${firstShown}–${lastShown}`}</span>
              )}
            </p>
            {searchResults.items.map((comic, idx) => (
              <button
                key={comic.id}
                type="button"
                data-testid={`search-result-${idx}`}
                onClick={() => handleSelectComic(comic)}
                className="w-full text-left bg-zinc-800/60 rounded-xl border border-zinc-700 p-3 hover:border-orange-500/40 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500"
                style={{ minHeight: '48px' }}
              >
                <div className="flex gap-3">
                  <CoverThumb src={comic.cover_url} alt={comic.title} className="w-12 h-16" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate text-sm">{comic.title}</h3>
                    <p className="text-xs text-zinc-400">
                      {comic.publisher_name || 'Unknown publisher'}
                      {displayYear(comic) ? ` · ${displayYear(comic)}` : ''}
                    </p>
                    {comic.variant_name && (
                      <p className="text-xs text-orange-300/80 truncate">{comic.variant_name}</p>
                    )}
                  </div>
                  <Plus size={18} className="text-orange-500 flex-shrink-0 mt-1" aria-hidden="true" />
                </div>
              </button>
            ))}

            {/* ⛔ THE HARD WALL THIS CLOSES. The search fetched page 1 and nothing rendered a way
                to ask for page 2, so of 5,727 matches exactly 20 were reachable and a book at
                result 21 could not be added to a vault at all. First/Last are here, not just
                Prev/Next, because 115 pages of Next is not "reachable" in any honest sense. */}
            {totalPages > 1 && (
              <nav
                className="flex items-center justify-between gap-2 pt-2"
                aria-label="Search result pages"
                data-testid="search-pager"
              >
                <div className="flex gap-1">
                  <button
                    type="button"
                    data-testid="search-page-first"
                    onClick={() => goToPage(1)}
                    disabled={page <= 1}
                    aria-label="First page of results"
                    className={pagerButtonClass}
                  >
                    «
                  </button>
                  <button
                    type="button"
                    data-testid="search-page-prev"
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    aria-label="Previous page of results"
                    className={pagerButtonClass}
                  >
                    ‹ Prev
                  </button>
                </div>

                {/* aria-live: a page turn replaces the whole list, and a screen-reader user who
                    hears nothing cannot tell a page change from a dead button. */}
                <span
                  className="text-xs text-zinc-400 tabular-nums"
                  data-testid="search-page-status"
                  aria-live="polite"
                >
                  {`Page ${page} of ${totalPages}`}
                </span>

                <div className="flex gap-1">
                  <button
                    type="button"
                    data-testid="search-page-next"
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages}
                    aria-label="Next page of results"
                    className={pagerButtonClass}
                  >
                    Next ›
                  </button>
                  <button
                    type="button"
                    data-testid="search-page-last"
                    onClick={() => goToPage(totalPages)}
                    disabled={page >= totalPages}
                    aria-label="Last page of results"
                    className={pagerButtonClass}
                  >
                    »
                  </button>
                </div>
              </nav>
            )}

            {/* Pagination makes every book REACHABLE; it does not make the right one easy to find.
                Ranking and refinement are the next slice, and until they land this line is what
                tells a user with 115 pages that narrowing is the faster road. */}
            {totalPages > 1 && (
              <p className="text-xs text-zinc-500 pt-1" data-testid="search-narrow-hint">
                Too many? Add the issue number or publisher to narrow it down.
              </p>
            )}
          </div>
        )}

        {!searchLoading && searchResults && searchResults.items.length === 0 && (
          <div data-testid="no-results" className="text-center py-8">
            <Search size={40} className="mx-auto text-zinc-600 mb-3" />
            <p className="text-zinc-400">No comics found</p>
            <p className="text-zinc-500 text-sm mt-1">Try a different search term</p>
            <button
              type="button"
              onClick={() => switchTab('manual')}
              className="mt-3 text-orange-400 hover:text-orange-300 text-sm"
              data-testid="no-results-manual"
            >
              Not in the catalog? Add it manually
            </button>
          </div>
        )}

        {!searchLoading && !searchResults && (
          <div className="text-center py-8">
            <Search size={40} className="mx-auto text-zinc-600 mb-3" />
            <p className="text-zinc-400">Search for a comic to add</p>
            <p className="text-zinc-500 text-sm mt-1">Enter a title, issue number, or barcode</p>
          </div>
        )}
      </div>
    );
  };

  const renderScanTab = () => (
    <div data-testid="scan-tab-content">
      {!scanResult && !scanLoading && !showBarcodeConfirm && (
        <div>
          {/* ⛔ ONE SCANNER FOR THE APP. This tab used the full-UI Html5QrcodeScanner with NO
              facingMode, so the camera was whatever the device listed first plus a remembered
              localStorage choice -- on a phone, usually the SELFIE camera. The shared component
              pins facingMode 'environment', serialises every start/stop so a remount cannot race
              a closing camera, and carries the flip + torch controls. */}
          <div data-testid="qr-reader" className="rounded-xl overflow-hidden mb-4">
            <BarcodeScanner onScan={handleDecoded} onError={handleScannerError} />
          </div>
          <p className="text-center text-zinc-400 text-sm">Point your camera at a barcode</p>
          {scanError && (
            <p className="text-center text-yellow-400 text-sm mt-2" data-testid="scan-error">{scanError}</p>
          )}
        </div>
      )}

      {scanLoading && (
        <div className="flex flex-col items-center py-12" role="status">
          <Loader2 size={32} className="animate-spin text-orange-500 mb-3" />
          <span className="text-zinc-400">Looking up barcode...</span>
        </div>
      )}

      {scanResult && !scanLoading && (
        <div className="space-y-3">
          {scanResult.not_found ? (
            <div data-testid="no-match-card" className="bg-zinc-800/60 rounded-xl border border-zinc-700 p-4 text-center">
              <p className="text-zinc-400">No match found for barcode {scanResult.barcode}</p>
              <button
                type="button"
                onClick={() => { switchTab('search'); setSearchQuery(scanResult.barcode); }}
                className="text-orange-400 hover:text-orange-300 text-sm mt-2"
                data-testid="no-match-search"
              >
                Search for it instead
              </button>
            </div>
          ) : (
            <div data-testid="auto-added-card" className="bg-green-900/20 rounded-xl border border-green-800/30 p-4 text-center">
              <p className="text-green-400 font-semibold mb-1">Added to Vault</p>
              <p className="text-white text-sm">{scanResult.title || 'Unknown Title'}</p>
            </div>
          )}
          <button
            type="button"
            data-testid="scan-again"
            onClick={() => setScanResult(null)}
            className="w-full bg-zinc-800 text-zinc-300 rounded-xl transition-colors hover:bg-zinc-700"
            style={{ height: '48px' }}
          >
            Scan Again
          </button>
        </div>
      )}
    </div>
  );

  const renderCaptureTab = () => (
    <div data-testid="capture-tab-content" className="space-y-4">
      <div className="bg-zinc-800/60 border border-zinc-700 rounded-2xl p-5 text-center space-y-3">
        <Camera size={32} className="mx-auto text-orange-500" aria-hidden="true" />
        <h2 className="text-white text-lg font-semibold">Photograph and grade it</h2>
        <p className="text-zinc-400 text-sm">
          Take a photo of the cover and Maestro grades the book, then you pick the matching
          issue from the catalog before it goes in your Vault.
        </p>
        <button
          type="button"
          data-testid="capture-goto-grading"
          onClick={() => {
            track('vault.quick_add.capture_handoff', 'vault', { destination: '/maestro/grade/upload' });
            onCapture();
          }}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold transition-colors"
          style={{ height: '48px' }}
        >
          Open the camera
        </button>
      </div>
      <p className="text-zinc-500 text-xs text-center">
        Already know the book? Use Scan for a barcode, or Search by title.
      </p>
    </div>
  );

  const renderManualTab = () => {
    if (added) return renderAddedCard();
    return (
      <form onSubmit={handleSubmitManual} className="space-y-4" data-testid="manual-form">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-sm text-yellow-200 flex items-start justify-between gap-3">
          <span>Not linked to the catalog — search first if the book exists.</span>
          <button
            type="button"
            onClick={() => switchTab('search')}
            className="text-orange-400 hover:text-orange-300 font-medium whitespace-nowrap"
            data-testid="manual-goto-search"
          >
            Search instead
          </button>
        </div>

        <div>
          <label htmlFor="manual-title" className="block text-sm text-zinc-400 mb-1">Title *</label>
          <input
            id="manual-title"
            data-testid="manual-title"
            type="text"
            value={manual.title}
            onChange={(e) => setManualField('title', e.target.value)}
            className={inputClass}
            style={selectStyle}
            aria-invalid={Boolean(submitError && submitError.field === 'title')}
            required
          />
          <FieldError error={submitError} name="title" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="manual-issue" className="block text-sm text-zinc-400 mb-1">Issue #</label>
            <input
              id="manual-issue"
              data-testid="manual-issue"
              type="text"
              value={manual.issue_number}
              onChange={(e) => setManualField('issue_number', e.target.value)}
              className={inputClass}
              style={selectStyle}
            />
            <FieldError error={submitError} name="issue_number" />
          </div>
          <div>
            <label htmlFor="manual-year" className="block text-sm text-zinc-400 mb-1">Year</label>
            <input
              id="manual-year"
              data-testid="manual-year"
              type="number"
              value={manual.year}
              onChange={(e) => setManualField('year', e.target.value)}
              className={inputClass}
              style={selectStyle}
            />
            <FieldError error={submitError} name="year" />
          </div>
        </div>

        <div>
          <label htmlFor="manual-publisher" className="block text-sm text-zinc-400 mb-1">Publisher</label>
          <input
            id="manual-publisher"
            data-testid="manual-publisher"
            type="text"
            value={manual.publisher}
            onChange={(e) => setManualField('publisher', e.target.value)}
            className={inputClass}
            style={selectStyle}
          />
          <FieldError error={submitError} name="publisher" />
        </div>

        <div>
          <label htmlFor="manual-grade" className="block text-sm text-zinc-400 mb-1">Your Grade</label>
          <input
            id="manual-grade"
            data-testid="manual-grade"
            type="text"
            value={manual.user_grade}
            onChange={(e) => setManualField('user_grade', e.target.value)}
            placeholder="e.g., 9.4 or NM"
            className={inputClass}
            style={selectStyle}
          />
          <FieldError error={submitError} name="user_grade" />
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="manual-is-graded"
              checked={manual.is_graded}
              onChange={(e) => setManualField('is_graded', e.target.checked)}
              className="w-4 h-4 rounded border-zinc-600"
            />
            <span className="text-sm text-zinc-300">Professionally Graded</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="manual-is-signed"
              checked={manual.is_signed}
              onChange={(e) => setManualField('is_signed', e.target.checked)}
              className="w-4 h-4 rounded border-zinc-600"
            />
            <span className="text-sm text-zinc-300">Signed</span>
          </label>
        </div>

        {manual.is_graded && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="manual-company" className="block text-sm text-zinc-400 mb-1">Grading Company</label>
              <select
                id="manual-company"
                data-testid="manual-company"
                value={manual.grading_company}
                onChange={(e) => setManualField('grading_company', e.target.value)}
                className={`${inputClass} appearance-none`}
                style={selectStyle}
              >
                <option value="">Select...</option>
                <option value="cgc">CGC</option>
                <option value="cbcs">CBCS</option>
                <option value="pgx">PGX</option>
              </select>
            </div>
            <div>
              <label htmlFor="manual-cert" className="block text-sm text-zinc-400 mb-1">Cert Number</label>
              <input
                id="manual-cert"
                data-testid="manual-cert"
                type="text"
                value={manual.cert_number}
                onChange={(e) => setManualField('cert_number', e.target.value)}
                className={inputClass}
                style={selectStyle}
              />
            </div>
          </div>
        )}

        {manual.is_signed && (
          <div>
            <label htmlFor="manual-signature" className="block text-sm text-zinc-400 mb-1">Signature Details</label>
            <input
              id="manual-signature"
              data-testid="manual-signature"
              type="text"
              value={manual.signature_details}
              onChange={(e) => setManualField('signature_details', e.target.value)}
              placeholder="e.g., Stan Lee, Todd McFarlane"
              className={inputClass}
              style={selectStyle}
            />
          </div>
        )}

        {submitError && (
          <p data-testid="submit-error" role="alert" className="text-red-400 text-sm">
            {submitError.message}
          </p>
        )}

        <button
          type="submit"
          data-testid="manual-submit"
          disabled={submitting || !manualTitleOk}
          className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
          style={{ minHeight: '48px' }}
        >
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          {submitting ? 'Saving...' : 'Save without linking'}
        </button>
      </form>
    );
  };

  return (
    <div data-testid="add-to-vault" className="text-white">
      <div data-testid="tab-bar" role="tablist" className="flex gap-2 mb-4 overflow-x-auto">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            data-testid={`tab-${id}`}
            onClick={() => switchTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl text-sm transition-colors px-3 ${
              activeTab === id
                ? 'bg-orange-500 text-white font-semibold'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
            style={{ minHeight: '48px', minWidth: '48px' }}
            aria-label={label}
            aria-pressed={activeTab === id}
            aria-selected={activeTab === id}
          >
            <Icon size={18} />
            <span className="whitespace-nowrap">{label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'search' && renderSearchTab()}
      {activeTab === 'scan' && renderScanTab()}
      {activeTab === 'capture' && renderCaptureTab()}
      {activeTab === 'bulk' && (
        <div data-testid="bulk-tab-content">
          <BulkBarcodeImport embedded onComplete={(result) => { if (typeof onAdded === 'function') onAdded(result); }} />
        </div>
      )}
      {activeTab === 'manual' && renderManualTab()}

      <BarcodeConfirmDialog
        isOpen={showBarcodeConfirm}
        onClose={closeBarcodeConfirm}
        barcode={pendingBarcode}
        candidates={barcodeCandidates}
        onConfirmed={(result) => {
          setScanResult({ added: true, title: result.title, vault_item_id: result.vault_item_id });
          if (typeof onAdded === 'function') onAdded(result);
        }}
        onManualEntry={(barcode) => {
          switchTab('search');
          setSearchQuery(barcode);
        }}
      />
    </div>
  );
}
